import { safeLocalStorage } from '../utils/safeStorage';
import type { ImportDiscardedRow, ImportEntitiesToCreate, ImportIndeterminateRow, ImportPreviewResult, ImportPreviewRow, ImportPreviewSummary, ImportReconciliationMapping, ImportRowCorrection } from '../types/imports';

// @ts-ignore - Vite env
const API_BASE_URL = (import.meta as any).env?.VITE_API_URL || 'http://localhost:3000/api';

function getAuthHeaders(): Record<string, string> {
    const token = safeLocalStorage.getItem('accessToken');
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    return headers;
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
    const resp = await fetch(`${API_BASE_URL}${path}`, {
        ...options,
        headers: { ...getAuthHeaders(), ...(options.headers as Record<string, string> ?? {}) },
    });
    if (!resp.ok) {
        const err = await resp.json().catch(() => ({ message: `HTTP ${resp.status}` }));
        throw new Error(err.message ?? `HTTP ${resp.status}`);
    }
    return resp.json();
}

async function requestMultipart<T>(path: string, formData: FormData): Promise<T> {
    const token = safeLocalStorage.getItem('accessToken');
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const resp = await fetch(`${API_BASE_URL}${path}`, { method: 'POST', headers, body: formData });
    if (!resp.ok) {
        const err = await resp.json().catch(() => ({ message: `HTTP ${resp.status}` }));
        throw new Error(err.message ?? `HTTP ${resp.status}`);
    }
    return resp.json();
}

async function downloadFile(path: string, filename: string): Promise<void> {
    const token = safeLocalStorage.getItem('accessToken');
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const resp = await fetch(`${API_BASE_URL}${path}`, { headers });
    if (!resp.ok) {
        const err = await resp.json().catch(() => ({ message: `HTTP ${resp.status}` }));
        throw new Error(err.message ?? `HTTP ${resp.status}`);
    }

    const blob = await resp.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
}

function audienceQuery({ planIds, signupFrom, signupTo }: AnnouncementAudienceFilter): string {
    const query = new URLSearchParams({ planIds: planIds.join(',') });
    if (signupFrom) query.set('signupFrom', signupFrom);
    if (signupTo) query.set('signupTo', signupTo);
    return query.toString();
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AdminSubscriptionSummary {
    planId: string;
    planName: string;
    status: string;
    startedAt: string;
    currentPeriodEnd?: string;
}

export interface AdminCustomer {
    id: string;
    name: string;
    email: string;
    whatsapp?: string | null;
    role: 'USER' | 'ADMIN';
    status: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED';
    createdAt: string;
    lastLoginAt?: string;
    subscription?: AdminSubscriptionSummary;
}

export interface SubscriptionEvent {
    id: string;
    type: string;
    fromPlanId?: string;
    fromPlanName?: string;
    toPlanId?: string;
    toPlanName?: string;
    actorUserId?: string;
    actorName?: string;
    note?: string;
    createdAt: string;
}

export interface FeatureAccessSummary {
    feature: string;
    lastAccess: string;
}

export interface AdminCustomerDetail extends AdminCustomer {
    subscription?: AdminSubscriptionSummary & { planId: string };
    subscriptionEvents?: SubscriptionEvent[];
    featureAccesses?: FeatureAccessSummary[];
}

export interface AdminImportBatch {
    id: string;
    fileName: string;
    status: 'PREVIEWED' | 'CONFIRMED' | 'FAILED' | 'CANCELED';
    totalLinhas: number;
    totalImportadas: number;
    totalIgnoradas: number;
    createdAt: string;
}

export type AdminImportBatchRow = ImportPreviewRow;
export type AdminImportBatchDiscardedRow = ImportDiscardedRow;

export interface AdminImportBatchRowsResponse {
    batchId: string;
    fileName: string;
    summary: { totalRows: number; totalValidRows: number; totalIgnoredRows: number; totalDuplicates: number };
    rows: AdminImportBatchRow[];
    discardedRows: AdminImportBatchDiscardedRow[];
}

export interface AdminCustomerDataSummary {
    userId: string;
    name: string;
    email: string;
    filiais: { id: string; name: string; outrosUsuarios: number }[];
    itens: { key: string; label: string; count: number }[];
    total: number;
}

export interface AdminResetDataResult {
    ok: true;
    deleted: Record<string, number>;
    total: number;
    filiaisReprovisionadas: number;
}

export interface AdminCustomerFilial {
    id: string;
    name: string;
    cnpj?: string | null;
    isActive?: boolean;
    empresaId?: string;
    role?: 'ADMIN' | 'EDITOR' | 'VIEWER';
}

export type AdminImportPreviewSummary = ImportPreviewSummary;
export type AdminImportEntitiesToCreate = ImportEntitiesToCreate;
export type AdminImportIndeterminateRow = ImportIndeterminateRow;
export type AdminImportPreviewResponse = ImportPreviewResult;

export interface AdminImportConfirmResponse {
    batchId: string;
    totalsCriados: number;
    dataMin?: string | null;
    dataMax?: string | null;
    entidadesCriadas: { categorias: number; subCategorias: number; formasPagamento: number };
    skippedDuplicates: number;
}

export interface AdminSubscription {
    id: string;
    userId: string;
    userName: string;
    userEmail: string;
    planId: string;
    planName: string;
    status: string;
    startedAt: string;
    currentPeriodEnd?: string;
}

export type AnnouncementStatus = 'DRAFT' | 'SENT' | 'RETIRED';

export interface AnnouncementRecord {
    id: string;
    title: string;
    previewText: string;
    status: AnnouncementStatus;
    planIds: string[];
    signupFrom: string | null;
    signupTo: string | null;
    recipients: number;
    sendEmail: boolean;
    sentAt: string | null;
    createdAt: string;
    updatedAt: string;
}

export interface AnnouncementListItem extends AnnouncementRecord {
    readCount: number;
    emails: Partial<Record<'PENDING' | 'SENT' | 'FAILED' | 'SKIPPED', number>>;
}

export interface Announcement extends AnnouncementRecord {
    contentHtml: string;
    /** Conteudo com as URLs assinadas dos arquivos, para o editor e a pre-visualizacao. */
    resolvedHtml: string;
}

export interface AnnouncementInput {
    title?: string;
    contentHtml?: string;
    previewText?: string;
    planIds?: string[];
    signupFrom?: string | null;
    signupTo?: string | null;
    sendEmail?: boolean;
}

export interface AnnouncementAudienceFilter {
    planIds: string[];
    signupFrom?: string | null;
    signupTo?: string | null;
}

export interface AnnouncementAudience {
    data: { id: string; name: string; email: string; planName: string | null; createdAt: string }[];
    pagination: { page: number; limit: number; total: number; totalPages: number };
}

export interface AnnouncementAssetUpload {
    id: string;
    kind: 'IMAGE' | 'DOCUMENT' | 'VIDEO';
    fileName: string;
    sizeBytes: number;
    url: string;
}

export interface AnnouncementVideoUpload {
    storageKey: string;
    uploadUrl: string;
    contentType: string;
}

export interface AdminPlan {
    id: string;
    name: string;
    description: string;
    price: number;
    /** 'yearly': `price` é o valor TOTAL cobrado no ano (não o equivalente mensal). */
    interval: 'monthly' | 'yearly';
    maxAccounts: number;
    maxTransactionsPerMonth: number;
    features: string[];
    isActive: boolean;
    externalRef?: string | null;
}

export interface TrackedFeature {
    id: string;
    key: string;
    label: string;
    description?: string;
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
}

export interface NotificationRule {
    id: string;
    name: string;
    description?: string;
    eventType: string;
    params: Record<string, unknown>;
    channels: string[];
    channelTemplates: Record<string, unknown>;
    isActive: boolean;
    cooldownDays: number;
    createdAt: string;
    updatedAt: string;
}

export interface NotificationDispatch {
    id: string;
    ruleId: string;
    ruleName: string;
    eventType?: string | null;
    userId: string;
    userName: string | null;
    userEmail: string | null;
    channel: string;
    status: 'PENDING' | 'SENT' | 'FAILED' | 'SKIPPED';
    errorMessage?: string | null;
    sentAt?: string | null;
    createdAt: string;
    payload?: Record<string, unknown> | null;
}

export type TemplateDefaults = Record<string, Record<string, { subject?: string; title?: string; body: string }>>;

export interface AdminDashboardKpis {
    totalUsers: number;
    activeUsers: number;
    inactiveUsers: number;
    suspendedUsers: number;
    totalSubscriptions: number;
    activeSubscriptions: number;
    trialSubscriptions: number;
    /** ACTIVE em plano pago (preço > 0). */
    paidActiveSubscriptions: number;
    paidMonthlySubscriptions: number;
    paidYearlySubscriptions: number;
    /** Vigentes sem pagamento: TRIAL ou plano gratuito. */
    testSubscriptions: number;
    expiringSoon: number;
    expiredSubscriptions: number;
    mrr: number;
    arr: number;
}

export type MetricsRange = '7d' | '30d' | '90d' | '12m';

export interface AdminMetricsResponse {
    range: string;
    granularity: 'day' | 'week' | 'month';
    from: string;
    to: string;
    newUsers: { date: string; count: number }[];
    newSubscriptions: { date: string; count: number }[];
    canceledSubscriptions: { date: string; count: number }[];
    mrr: { date: string; value: number }[];
    arr: { date: string; value: number }[];
    churnRate: { date: string; value: number }[];
    subscriptionsByStatus: { status: string; count: number }[];
    usersByPlan: { planName: string; count: number }[];
    topFeatures: { feature: string; count: number }[];
    activeUsers: { dau: number; wau: number; mau: number };
    notifications: { date: string; channel: string; status: string; count: number }[];
}

export interface NotificationSettings {
    email: {
        configured: boolean;
        fromEmail: string;
        fromName: string;
        hasApiKey: boolean;
        hasSecretKey: boolean;
        apiKey: string;
        secretKey: string;
    };
    whatsapp: {
        configured: boolean;
        url: string;
        hasToken: boolean;
        token: string;
    };
    webhook: {
        configured: boolean;
        url: string;
    };
}

export interface UpdateNotificationSettingsPayload {
    mailjetApiKey?: string;
    mailjetSecretKey?: string;
    mailjetFromEmail?: string;
    mailjetFromName?: string;
    conjochatToken?: string;
    conjochatUrl?: string;
    webhookUrl?: string;
}

export interface OnboardingConfig {
    videoUrl: string | null;
}

export interface OnboardingSurveyRow {
    id: string;
    userId: string;
    userName: string;
    userEmail: string;
    completedAt: string;
    objetivo: string[];
    objetivoOutro?: string | null;
    controleFinanceiro: string;
    controleFinanceiroOutro?: string | null;
    segmento: string;
    segmentoOutro?: string | null;
    faturamentoAnual: string;
    numeroFuncionarios: string;
    comoConheceu: string[];
    comoConheceuOutro?: string | null;
}

export interface OnboardingSurveyListResponse {
    data: OnboardingSurveyRow[];
    pagination: { page: number; limit: number; total: number; totalPages: number };
}

export interface OnboardingSurveyChartPoint {
    label: string;
    value: number;
    pct: number;
}

export interface OnboardingSurveyStats {
    kpis: {
        totalRespostas: number;
        principalCanalAquisicao: OnboardingSurveyChartPoint;
        segmentoMaisComum: OnboardingSurveyChartPoint;
    };
    charts: {
        comoConheceu: OnboardingSurveyChartPoint[];
        segmento: OnboardingSurveyChartPoint[];
        objetivo: OnboardingSurveyChartPoint[];
        faturamentoAnual: OnboardingSurveyChartPoint[];
    };
}

// ---------------------------------------------------------------------------
// API client
// ---------------------------------------------------------------------------

export const adminApi = {
    // Dashboard
    getDashboardKpis: (): Promise<AdminDashboardKpis> =>
        request('/admin/dashboard'),

    getMetrics: (range: MetricsRange): Promise<AdminMetricsResponse> =>
        request(`/admin/metrics?range=${range}`),

    // Notification Settings
    getNotificationSettings: (): Promise<NotificationSettings> =>
        request('/admin/settings/notifications'),

    testEmail: (to: string): Promise<{ ok: boolean; message: string }> =>
        request('/admin/settings/test-email', { method: 'POST', body: JSON.stringify({ to }) }),

    testWhatsapp: (to: string): Promise<{ ok: boolean; message: string }> =>
        request('/admin/settings/test-whatsapp', { method: 'POST', body: JSON.stringify({ to }) }),

    testWhatsappTemplate: (to: string, templateName: string, templateParams: string[], language: string): Promise<{ ok: boolean; message: string }> =>
        request('/admin/settings/test-whatsapp-template', { method: 'POST', body: JSON.stringify({ to, templateName, templateParams, language }) }),

    testWebhook: (url?: string): Promise<{ ok: boolean; message: string }> =>
        request('/admin/settings/test-webhook', { method: 'POST', body: JSON.stringify({ url }) }),

    updateNotificationSettings: (payload: UpdateNotificationSettingsPayload): Promise<NotificationSettings> =>
        request('/admin/settings/notifications', { method: 'PATCH', body: JSON.stringify(payload) }),

    // Onboarding Settings
    getOnboardingConfig: (): Promise<OnboardingConfig> =>
        request('/admin/settings/onboarding'),

    updateOnboardingConfig: (data: { videoUrl: string }): Promise<OnboardingConfig> =>
        request('/admin/settings/onboarding', { method: 'PATCH', body: JSON.stringify(data) }),

    // Onboarding Survey
    listOnboardingSurveys: (params?: { page?: number; limit?: number; search?: string }): Promise<OnboardingSurveyListResponse> => {
        const q = new URLSearchParams();
        if (params?.page) q.set('page', String(params.page));
        if (params?.limit) q.set('limit', String(params.limit));
        if (params?.search) q.set('search', params.search);
        return request(`/admin/onboarding-survey${q.toString() ? `?${q}` : ''}`);
    },

    getOnboardingSurveyStats: (): Promise<OnboardingSurveyStats> =>
        request('/admin/onboarding-survey/stats'),

    exportOnboardingSurveys: (params?: { startDate?: string; endDate?: string; search?: string }): Promise<void> => {
        const q = new URLSearchParams();
        if (params?.startDate) q.set('startDate', params.startDate);
        if (params?.endDate) q.set('endDate', params.endDate);
        if (params?.search) q.set('search', params.search);
        const filename = `pesquisa-onboarding-${Date.now()}.xlsx`;
        return downloadFile(`/admin/onboarding-survey/export${q.toString() ? `?${q}` : ''}`, filename);
    },

    // Customers
    listCustomers: (params?: { status?: string; search?: string }): Promise<AdminCustomer[]> => {
        const q = new URLSearchParams();
        if (params?.status) q.set('status', params.status);
        if (params?.search) q.set('search', params.search);
        return request(`/admin/customers${q.toString() ? `?${q}` : ''}`);
    },

    getCustomerDetail: (id: string): Promise<AdminCustomerDetail> =>
        request(`/admin/customers/${id}`),

    updateCustomerStatus: (id: string, status: string): Promise<void> =>
        request(`/admin/customers/${id}/status`, { method: 'PATCH', body: JSON.stringify({ status }) }),

    updateCustomer: (id: string, data: { name?: string; email?: string; whatsapp?: string; status?: string }): Promise<AdminCustomer> =>
        request(`/admin/customers/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),

    createCustomer: (data: { name: string; email: string; password: string; whatsapp?: string }): Promise<AdminCustomer> =>
        request('/admin/customers', { method: 'POST', body: JSON.stringify(data) }),

    deleteCustomer: (id: string): Promise<void> =>
        request(`/admin/customers/${id}`, { method: 'DELETE' }),

    setCustomerPassword: (id: string, newPassword: string): Promise<{ ok: true }> =>
        request(`/admin/customers/${id}/password`, { method: 'PATCH', body: JSON.stringify({ newPassword }) }),

    getCustomerDataSummary: (id: string): Promise<AdminCustomerDataSummary> =>
        request(`/admin/customers/${id}/data-summary`),

    resetCustomerData: (id: string, confirmEmail: string): Promise<AdminResetDataResult> =>
        request(`/admin/customers/${id}/reset-data`, { method: 'POST', body: JSON.stringify({ confirmEmail }) }),

    getCustomerImportBatches: (id: string): Promise<AdminImportBatch[]> =>
        request(`/admin/customers/${id}/import-batches`),

    getCustomerImportBatchRows: (id: string, batchId: string): Promise<AdminImportBatchRowsResponse> =>
        request(`/admin/customers/${id}/import-batches/${batchId}/rows`),

    getCustomerFiliais: (id: string): Promise<AdminCustomerFilial[]> =>
        request(`/admin/customers/${id}/filiais`),

    createCustomerFilial: (id: string, data: { name: string; cnpj?: string }): Promise<AdminCustomerFilial> =>
        request(`/admin/customers/${id}/filiais`, { method: 'POST', body: JSON.stringify(data) }),

    updateCustomerFilial: (id: string, filialId: string, data: { name?: string; cnpj?: string; isActive?: boolean }): Promise<AdminCustomerFilial> =>
        request(`/admin/customers/${id}/filiais/${filialId}`, { method: 'PATCH', body: JSON.stringify(data) }),

    deleteCustomerFilial: (id: string, filialId: string): Promise<{ success: boolean }> =>
        request(`/admin/customers/${id}/filiais/${filialId}`, { method: 'DELETE' }),

    previewCustomerImportBatch: (id: string, file: File, filialId?: string): Promise<AdminImportPreviewResponse> => {
        const formData = new FormData();
        formData.append('file', file);
        const q = filialId ? `?filialId=${encodeURIComponent(filialId)}` : '';
        return requestMultipart(`/admin/customers/${id}/import-batches/preview${q}`, formData);
    },

    getPendingCustomerImportPreview: (id: string): Promise<AdminImportPreviewResponse | null> =>
        request(`/admin/customers/${id}/import-batches/pending`),

    confirmCustomerImportBatch: (id: string, data: {
        batchPreviewId: string;
        conflictResolution?: 'SIGN' | 'CATEGORY';
        ignoreDuplicates?: boolean;
        keepDuplicateRowNumbers?: number[];
        resolutions?: { subCategoryTypes?: Record<string, 'ENTRADA' | 'SAIDA'> };
        mapping?: ImportReconciliationMapping;
    }): Promise<AdminImportConfirmResponse> =>
        request(`/admin/customers/${id}/import-batches/confirm`, { method: 'POST', body: JSON.stringify(data) }),

    deleteCustomerImportBatch: (id: string, batchId: string): Promise<{ deleted: number; batchId: string }> =>
        request(`/admin/customers/${id}/import-batches/${batchId}`, { method: 'DELETE' }),

    fixCustomerImportBatchRows: (id: string, batchId: string, corrections: ImportRowCorrection[]): Promise<AdminImportPreviewResponse> =>
        request(`/admin/customers/${id}/import-batches/${batchId}/rows`, { method: 'PATCH', body: JSON.stringify({ corrections }) }),

    downloadCustomerImportDiscardedRows: (id: string, batchId: string): Promise<void> =>
        downloadFile(`/admin/customers/${id}/import-batches/${batchId}/discarded-rows/export`, `linhas-com-erro-${batchId}.xlsx`),

    // Subscriptions
    listSubscriptions: (): Promise<AdminSubscription[]> =>
        request('/admin/subscriptions'),

    assignPlan: (userId: string, data: { planId: string }): Promise<void> =>
        request(`/admin/subscriptions/${userId}/assign-plan`, { method: 'POST', body: JSON.stringify(data) }),

    renewSubscription: (userId: string, data: { expiresAt: string }): Promise<void> =>
        request(`/admin/subscriptions/${userId}/renew`, { method: 'POST', body: JSON.stringify(data) }),

    cancelSubscription: (userId: string): Promise<void> =>
        request(`/admin/subscriptions/${userId}/cancel`, { method: 'POST' }),

    suspendSubscription: (userId: string): Promise<void> =>
        request(`/admin/subscriptions/${userId}/suspend`, { method: 'POST' }),

    reactivateSubscription: (userId: string): Promise<void> =>
        request(`/admin/subscriptions/${userId}/reactivate`, { method: 'POST' }),

    // Comunicados
    listAnnouncements: (): Promise<AnnouncementListItem[]> =>
        request('/admin/announcements'),

    getAnnouncement: (id: string): Promise<Announcement> =>
        request(`/admin/announcements/${id}`),

    createAnnouncement: (data: AnnouncementInput): Promise<AnnouncementRecord> =>
        request('/admin/announcements', { method: 'POST', body: JSON.stringify(data) }),

    updateAnnouncement: (id: string, data: AnnouncementInput): Promise<AnnouncementRecord> =>
        request(`/admin/announcements/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),

    getAnnouncementAudienceCount: (filter: AnnouncementAudienceFilter): Promise<{ count: number }> =>
        request(`/admin/announcements/audience-count?${audienceQuery(filter)}`),

    getAnnouncementAudience: (filter: AnnouncementAudienceFilter, page: number): Promise<AnnouncementAudience> =>
        request(`/admin/announcements/audience?${audienceQuery(filter)}&page=${page}`),

    sendAnnouncementTest: (id: string): Promise<{ sent: boolean }> =>
        request(`/admin/announcements/${id}/test`, { method: 'POST' }),

    sendAnnouncement: (id: string): Promise<AnnouncementRecord> =>
        request(`/admin/announcements/${id}/send`, { method: 'POST' }),

    retireAnnouncement: (id: string): Promise<AnnouncementRecord> =>
        request(`/admin/announcements/${id}/retire`, { method: 'POST' }),

    uploadAnnouncementAsset: (file: File): Promise<AnnouncementAssetUpload> => {
        const formData = new FormData();
        formData.append('file', file);
        return requestMultipart('/admin/announcements/assets', formData);
    },

    createAnnouncementVideoUpload: (data: { fileName: string; sizeBytes: number }): Promise<AnnouncementVideoUpload> =>
        request('/admin/announcements/assets/video/upload-url', { method: 'POST', body: JSON.stringify(data) }),

    completeAnnouncementVideoUpload: (data: { storageKey: string; fileName: string }): Promise<AnnouncementAssetUpload> =>
        request('/admin/announcements/assets/video/complete', { method: 'POST', body: JSON.stringify(data) }),

    // Plans
    listPlans: (): Promise<AdminPlan[]> =>
        request('/admin/plans'),

    createPlan: (data: Partial<AdminPlan>): Promise<AdminPlan> =>
        request('/admin/plans', { method: 'POST', body: JSON.stringify(data) }),

    updatePlan: (id: string, data: Partial<AdminPlan>): Promise<AdminPlan> =>
        request(`/admin/plans/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),

    deletePlan: (id: string): Promise<void> =>
        request(`/admin/plans/${id}`, { method: 'DELETE' }),

    // Notification Rules
    listNotificationRules: (): Promise<NotificationRule[]> =>
        request('/admin/notifications/rules'),

    createNotificationRule: (data: Partial<NotificationRule>): Promise<NotificationRule> =>
        request('/admin/notifications/rules', { method: 'POST', body: JSON.stringify(data) }),

    updateNotificationRule: (id: string, data: Partial<NotificationRule>): Promise<NotificationRule> =>
        request(`/admin/notifications/rules/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),

    deleteNotificationRule: (id: string): Promise<void> =>
        request(`/admin/notifications/rules/${id}`, { method: 'DELETE' }),

    runNotifications: (params?: { ruleId?: string; force?: boolean }): Promise<{ status: string; force: boolean; rulesRun: Array<{ id: string; name: string }> }> =>
        request('/admin/notifications/run', { method: 'POST', body: JSON.stringify(params ?? {}) }),

    getTemplateDefaults: (): Promise<TemplateDefaults> =>
        request('/admin/notifications/template-defaults'),

    // Tracked Features
    listTrackedFeatures: (): Promise<TrackedFeature[]> =>
        request('/admin/notifications/tracked-features'),

    updateTrackedFeature: (id: string, data: Partial<TrackedFeature>): Promise<TrackedFeature> =>
        request(`/admin/notifications/tracked-features/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),

    // Dispatches
    listDispatches: (params?: { ruleId?: string; userId?: string }): Promise<NotificationDispatch[]> => {
        const q = new URLSearchParams();
        if (params?.ruleId) q.set('ruleId', params.ruleId);
        if (params?.userId) q.set('userId', params.userId);
        return request(`/admin/notifications/dispatches${q.toString() ? `?${q}` : ''}`);
    },
};
